import { useLanguageStore } from '$/store/languageStore';
import { getErrorMsgByNoAmount } from '$/api/server';
import { openVipModal } from '$/store/useVipModalStore';
import { createAiToolTask, queryAiToolTask } from '@/domains/tools/toolsService';
import type {
  AiToolSubmitParamWithCategory,
  AiToolQueryResult,
  AiToolResultImageItem,
  AiToolSubmitParamsFor,
  AiToolTaskResultItem,
  CreateAiToolTaskItemResult,
  TaskCategory,
} from '@/domains/tools/types';

const POLL_INTERVAL_MS = 2000;

/** 任务成功状态码 */
const STATUS_SUCCESS = 100;
/** 失败状态码下限（90 <= status < 100 为失败） */
const STATUS_FAIL_MIN = 90;
/** 权益扣减失败状态码，与画布 taskQuery 的订阅引导协议保持一致。 */
const STATUS_INSUFFICIENT_ENTITLEMENT = 95;

/**
 * 从 smartcatTranslation 读取 AI 工具任务通用失败兜底文案。
 */
function getProcessingFailedText() {
  return useLanguageStore.getState().languageStore.smartcatTranslationText((v) => v.processing_failed);
}

/**
 * 从单条提交参数中读取任务类型，兼容旧调用只在外层传 task_category 的写法。
 */
function getSubmitTaskCategory<T extends TaskCategory>(
  task: AiToolSubmitParamsFor<T> | AiToolSubmitParamWithCategory,
  fallback?: T,
): TaskCategory {
  return 'task_category' in task ? task.task_category : (fallback || 'eraser');
}

/**
 * 兼容 submit.json 新旧返回，统一提取 msgid 与逐项错误。
 */
function normalizeCreateTaskItemResult(item?: string | CreateAiToolTaskItemResult) {
  if (typeof item === 'string') {
    return { success: Boolean(item), msg: item ? '' : '创建任务未返回 msgid', msg_id: item };
  }
  if (item) return item;
  return { success: false, msg: '创建任务未返回 ids', msg_id: '' };
}

/**
 * 延迟指定毫秒
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * 单条任务是否已结束（成功或失败）
 */
function isTaskTerminal(status: number): boolean {
  return status === STATUS_SUCCESS || (status >= STATUS_FAIL_MIN && status < STATUS_SUCCESS);
}

/**
 * 查询权益扣减失败引导并打开订阅弹窗；引导查询失败时仍保留基础订阅入口。
 */
async function openInsufficientEntitlementSubscribe(taskId: string): Promise<void> {
  let reduceFailGuide: string | undefined;
  try {
    const response = await getErrorMsgByNoAmount(taskId);
    reduceFailGuide = response?.reduce_fail_guide;
  } catch (error) {
    console.error('查询 AI 工具额度不足订阅引导失败:', error);
  }
  openVipModal('topright_page', reduceFailGuide);
}

/** 统一入参：params 为 前端id -> 任务参数，单张一个 key，多张多个 key */
export interface GetAiToolTaskResultOptions<T extends TaskCategory> {
  task_category?: T;
  params: Record<string, AiToolSubmitParamsFor<T> | AiToolSubmitParamWithCategory>;
  /** 创建任务并拿到后端 msgid 后触发，用于按点击生成规范上报 task_id。 */
  onTaskCreated?: (ids: Record<string, string>) => void;
  /** 每轮询到一条任务结果时回调，用于按 id 匹配并更新对应图片状态 */
  onTaskUpdate?: (frontendId: string, item: AiToolTaskResultItem) => void;
}

export interface GetAiToolTaskResultSuccess {
  success: true;
  data: AiToolQueryResult;
  /** 前端 id -> 服务端 msgid */
  ids: Record<string, string>;
}

export interface GetAiToolTaskResultFailure {
  success: false;
  data: AiToolQueryResult;
  message: string;
}

export type GetAiToolTaskResultResult = GetAiToolTaskResultSuccess | GetAiToolTaskResultFailure;

/**
 * 创建 AI 工具任务并轮询直到成功或失败。
 * 服务端查询返回 data 为数组，按 id（msgid）匹配前端 id；跨轮次缓存已返回结果，未完成的继续轮询。
 */
export async function getAiToolTaskResult<T extends TaskCategory>(
  options: GetAiToolTaskResultOptions<T>,
): Promise<GetAiToolTaskResultResult> {
  const {
    task_category, params, onTaskCreated, onTaskUpdate,
  } = options;
  const keys = Object.keys(params);
  if (keys.length === 0) {
    return {
      success: false,
      data: {} as AiToolQueryResult,
      message: 'params 为空',
    };
  }
  const fallbackTaskCategory = getSubmitTaskCategory(params[keys[0]], task_category);

  const { data: { ids } } = await createAiToolTask(task_category, params);
  if (!ids || typeof ids !== 'object') {
    return {
      success: false,
      data: {} as AiToolQueryResult,
      message: '创建任务未返回 ids',
    };
  }

  const submitFailures: Record<string, string> = {};
  const normalizedIds: Record<string, string> = {};
  keys.forEach((key) => {
    const itemResult = normalizeCreateTaskItemResult(ids[key]);
    if (itemResult.success && itemResult.msg_id) {
      normalizedIds[key] = itemResult.msg_id;
      return;
    }
    submitFailures[key] = itemResult.msg || '创建任务失败';
    onTaskUpdate?.(key, {
      id: itemResult.msg_id,
      status: STATUS_FAIL_MIN,
      fail_msg: submitFailures[key],
      task_category: getSubmitTaskCategory(params[key], task_category),
      result_images: [],
    });
  });

  const successfulKeys = Object.keys(normalizedIds);
  const orderedIds = successfulKeys.map((k) => normalizedIds[k]).filter(Boolean);
  if (orderedIds.length === 0) {
    const message = Object.values(submitFailures)[0] || '创建任务未返回 msgid';
    return {
      success: false,
      data: {
        status: String(STATUS_FAIL_MIN),
        fail_msg: message,
        task_category: fallbackTaskCategory,
        init_image: [],
        result: {},
      },
      message,
    };
  }
  try {
    onTaskCreated?.(normalizedIds);
  } catch (error) {
    console.error('AI工具任务创建埋点回调失败:', error);
  }

  const msgidToFrontendId = Object.fromEntries(
    Object.entries(normalizedIds).map(([fid, msgid]) => [msgid, fid]),
  );
  const taskResultCache = new Map<string, AiToolTaskResultItem>();
  let hasOpenedInsufficientEntitlementSubscribe = false;

  const poll = async (idsToPoll: string[]): Promise<GetAiToolTaskResultResult> => {
    const { data } = await queryAiToolTask(idsToPoll.join(','));
    if (!Array.isArray(data)) {
      return {
        success: false,
        data: {} as AiToolQueryResult,
        message: '查询未返回 data 数组',
      };
    }

    const remainingIds: string[] = [];
    const returnedIds = new Set<string>();
    for (const item of data) {
      returnedIds.add(item.id);
      taskResultCache.set(item.id, item);

      const frontendId = msgidToFrontendId[item.id];
      if (frontendId) {
        onTaskUpdate?.(frontendId, item);
      }
      const terminal = isTaskTerminal(item.status);
      if (!terminal) {
        remainingIds.push(item.id);
      }
    }
    const insufficientEntitlementTask = data.find(
      (item) => item.status === STATUS_INSUFFICIENT_ENTITLEMENT,
    );
    if (insufficientEntitlementTask && !hasOpenedInsufficientEntitlementSubscribe) {
      hasOpenedInsufficientEntitlementSubscribe = true;
      await openInsufficientEntitlementSubscribe(insufficientEntitlementTask.id);
    }
    idsToPoll.forEach((id) => {
      if (!returnedIds.has(id)) {
        remainingIds.push(id);
      }
    });

    if (remainingIds.length > 0) {
      await delay(POLL_INTERVAL_MS);
      return poll(remainingIds);
    }

    const result: Record<string, AiToolResultImageItem[]> = {};
    for (const k of successfulKeys) {
      const msgid = normalizedIds[k];
      const raw = taskResultCache.get(msgid);
      if (!raw) {
        return {
          success: false,
          data: {
            status: '',
            fail_msg: '任务结果缺失',
            task_category: getSubmitTaskCategory(params[k], task_category),
            init_image: [],
            result: {},
          },
          message: '任务结果缺失',
        };
      }
      if (raw.status !== STATUS_SUCCESS) {
        return {
          success: false,
          data: {
            status: String(raw.status),
            fail_msg: raw.fail_msg || '',
            task_category: raw.task_category || getSubmitTaskCategory(params[k], task_category),
            init_image: [],
            result,
          },
          message: raw.fail_msg || getProcessingFailedText(),
        };
      }
      result[k] = (raw.result_images ?? []) as AiToolResultImageItem[];
    }

    if (Object.keys(submitFailures).length > 0) {
      const message = Object.values(submitFailures)[0] || '部分任务创建失败';
      return {
        success: false,
        data: {
          status: String(STATUS_FAIL_MIN),
          fail_msg: message,
          task_category: fallbackTaskCategory,
          init_image: [],
          result,
        },
        message,
      };
    }

    return {
      success: true,
      data: {
        status: String(STATUS_SUCCESS),
        fail_msg: '',
        task_category: fallbackTaskCategory,
        init_image: [],
        result,
      },
      ids: normalizedIds,
    };
  };

  return poll(orderedIds);
}
